<template>
    <DashboardLayout title="Services">
        <div class="py-6 px-4 sm:px-6 lg:px-8">
            <!-- Header -->
            <div class="sm:flex sm:items-center sm:justify-between mb-6">
                <div>
                    <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Services</h1>
                    <p class="mt-2 text-sm text-gray-700">
                        Manage services that can be assigned to work orders.
                    </p>
                </div>
                <div class="mt-4 sm:mt-0">
                    <Link
                        :href="route('settings.services.create')"
                        class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        <svg class="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                        </svg>
                        Add Service
                    </Link>
                </div>
            </div>

            <!-- Filters -->
            <div class="bg-white shadow rounded-lg mb-6">
                <div class="px-6 py-4">
                    <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <!-- Search -->
                        <div>
                            <label for="search" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Search</label>
                            <input
                                id="search"
                                v-model="search"
                                type="text"
                                placeholder="Search services..."
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            />
                        </div>

                        <!-- Category Filter -->
                        <div>
                            <label for="category" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Category</label>
                            <select
                                id="category"
                                v-model="categoryFilter"
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            >
                                <option value="">All Categories</option>
                                <option v-for="category in categories" :key="category.id" :value="category.id">
                                    {{ category.name }}
                                </option>
                            </select>
                        </div>

                        <!-- Status Filter -->
                        <div>
                            <label for="status" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                            <select
                                id="status"
                                v-model="activeFilter"
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            >
                                <option value="">All Statuses</option>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </select>
                        </div>
                    </div>

                    <!-- Reset Filters -->
                    <div class="mt-4 flex justify-end" v-if="hasFilters">
                        <button
                            @click="resetFilters"
                            class="text-sm text-gray-600 hover:text-gray-900"
                        >
                            Reset Filters
                        </button>
                    </div>
                </div>
            </div>

            <!-- Services Table -->
            <div class="bg-white shadow rounded-lg overflow-hidden">
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead class="bg-gray-50">
                            <tr>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Service Name
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Category
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Est. Duration
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Est. Cost
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Work Orders
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200 dark:divide-gray-700">
                            <tr v-if="services.data.length === 0">
                                <td colspan="7" class="px-6 py-12 text-center text-sm text-gray-500">
                                    No services found.
                                    <Link :href="route('settings.services.create')" class="text-blue-600 hover:text-blue-900">
                                        Create your first service
                                    </Link>
                                </td>
                            </tr>
                            <tr v-for="service in services.data" :key="service.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <!-- Service Name -->
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm font-medium text-gray-900">{{ service.name }}</div>
                                    <div v-if="service.description" class="text-sm text-gray-500 truncate max-w-xs">
                                        {{ service.description }}
                                    </div>
                                </td>

                                <!-- Category -->
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm text-gray-900">{{ service.category?.name || 'N/A' }}</div>
                                </td>

                                <!-- Estimated Duration -->
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm text-gray-900">
                                        {{ service.estimated_duration_hours ? `${service.estimated_duration_hours} hrs` : 'N/A' }}
                                    </div>
                                </td>

                                <!-- Estimated Cost -->
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm text-gray-900">
                                        {{ service.estimated_cost ? formatMoney(service.estimated_cost) : 'N/A' }}
                                    </div>
                                </td>

                                <!-- Work Orders Count -->
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm text-gray-900">{{ service.work_orders_count || 0 }}</div>
                                </td>

                                <!-- Status -->
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        :class="[
                                            'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                                            service.is_active
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-gray-100 text-gray-800'
                                        ]"
                                    >
                                        {{ service.is_active ? 'Active' : 'Inactive' }}
                                    </span>
                                </td>

                                <!-- Actions -->
                                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div class="flex items-center justify-end space-x-3">
                                        <Link
                                            :href="route('settings.services.edit', service.id)"
                                            class="text-blue-600 hover:text-blue-900"
                                            title="Edit"
                                        >
                                            Edit
                                        </Link>
                                        <button
                                            @click="toggleActive(service)"
                                            class="text-gray-600 hover:text-gray-900"
                                            :title="service.is_active ? 'Deactivate' : 'Activate'"
                                        >
                                            {{ service.is_active ? 'Deactivate' : 'Activate' }}
                                        </button>
                                        <button
                                            v-if="service.work_orders_count === 0"
                                            @click="deleteService(service)"
                                            class="text-red-600 hover:text-red-900"
                                            title="Delete"
                                        >
                                            Delete
                                        </button>
                                        <span
                                            v-else
                                            class="text-gray-400 cursor-not-allowed"
                                            title="Cannot delete service with work orders"
                                        >
                                            Delete
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination -->
                <div v-if="services.data.length > 0" class="px-6 py-4 border-t border-gray-200">
                    <Pagination :links="services.links" />
                </div>
            </div>
        </div>
    </DashboardLayout>
</template>

<script setup>
import { ref, watch } from 'vue';
import { router, Link } from '@inertiajs/vue3';
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import Pagination from '@/Components/Pagination.vue';
import { useToast } from '@/Composables/useToast';
import { useCurrency } from '@/Composables/useCurrency';

const props = defineProps({
    services: {
        type: Object,
        required: true
    },
    categories: {
        type: Array,
        required: true
    },
    filters: {
        type: Object,
        required: true
    }
});

const toast = useToast();
const { formatMoney } = useCurrency();

// Filter state
const search = ref(props.filters.search || '');
const activeFilter = ref(props.filters.active || '');
const categoryFilter = ref(props.filters.category || '');

// Computed property to check if any filters are active
const hasFilters = ref(false);

// Watch filters and update URL with debounce
let searchTimeout = null;

watch([search, activeFilter, categoryFilter], () => {
    hasFilters.value = search.value || activeFilter.value || categoryFilter.value;

    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        router.get(route('settings.services.index'), {
            search: search.value,
            active: activeFilter.value,
            category: categoryFilter.value
        }, {
            preserveState: true,
            preserveScroll: true,
            replace: true
        });
    }, 300);
});

const resetFilters = () => {
    search.value = '';
    activeFilter.value = '';
    categoryFilter.value = '';
};

const toggleActive = (service) => {
    if (confirm(`Are you sure you want to ${service.is_active ? 'deactivate' : 'activate'} this service?`)) {
        router.post(route('settings.services.toggleActive', service.id), {}, {
            preserveScroll: true,
            onSuccess: () => {
                toast.success(`Service ${service.is_active ? 'deactivated' : 'activated'} successfully!`);
            },
            onError: () => {
                toast.error('Failed to update service status.');
            }
        });
    }
};

const deleteService = (service) => {
    if (confirm(`Are you sure you want to delete "${service.name}"? This action cannot be undone.`)) {
        router.delete(route('settings.services.destroy', service.id), {
            preserveScroll: true,
            onSuccess: () => {
                toast.success('Service deleted successfully!');
            },
            onError: () => {
                toast.error('Failed to delete service.');
            }
        });
    }
};
</script>
